import React, { useEffect, useMemo, useState } from "react"

import { css } from "@emotion/react"
import { Progress } from "antd"

import type { IIndexable } from "@common-shared/types/general.types.ts"

import type { IStepperStep } from "@front-shared/types/objects.types.ts"
import type { IStepperStepProps } from "@front-shared/types/props.types.ts"

import { FRONT_GENERAL_CONFIG } from "../config/general.config"
import { NIRVANA_COLORS } from "../config/theme.config.ts"
import { UtilHelper } from "../helpers/util.helper"
import { useIsMobile } from "../hooks/isMobile.hook"
import { PrimaryText } from "./primaryText.element"

export const StepperElement: React.FunctionComponent<IStepperProps> = props => {
    const isMobile = useIsMobile()

    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0)
    const [stepperData, setStepperData] = useState<any>({})

    const [stepsProcessed, setStepsProcessed] = useState<boolean>(false)

    // You can provide specific step via ?step=1 in url
    // This will allow to submit just one step from stepper
    // For example for to do section in dashboard
    const [specificStep, setSpecificStep] = useState<number>()

    const percentageOfProgress = useMemo<number>(() => {
        return Number(
            ((currentStepIndex / (props.steps.length || 1)) * 100).toFixed(0),
        )
    }, [currentStepIndex, props.steps])

    // On mobile devices scroll to top on each step, otherwise scroll position is wrong
    useEffect(() => {
        if (!isMobile) {
            return
        }

        setTimeout(UtilHelper.scrollToPageTop)
    }, [currentStepIndex])

    const goForward = (newData: any = null) => {
        // With specific step set we are just submitting that step
        if (
            currentStepIndex < props.steps.length - 1 &&
            typeof specificStep !== "number"
        ) {
            setCurrentStepIndex(currentStepIndex + 1)
        } else {
            props.onStepperSubmit(newData)
        }
    }

    const currentActiveStep: IStepperStep = useMemo(() => {
        const newStep = props.steps[currentStepIndex]

        if (newStep.isHidden) {
            goForward(stepperData)
        }

        return newStep
    }, [currentStepIndex, props.steps])

    useEffect(() => {
        if (!props.searchParams) {
            setStepsProcessed(true)

            return
        }

        try {
            const neededStep = props.searchParams.get(
                FRONT_GENERAL_CONFIG.urlSearchParamsKeys.step,
            )

            if (neededStep && props.steps[parseInt(neededStep)]) {
                setSpecificStep(parseInt(neededStep))
                setCurrentStepIndex(parseInt(neededStep))
            }
        } catch (e) {
        } finally {
            setStepsProcessed(true)
        }
    }, [props.steps])

    const onStepSubmit = (data: any, changeStep = true) => {
        if (!currentActiveStep) {
            return
        }

        const key = currentActiveStep.key

        let newData = {} as IIndexable
        newData[key] = data

        newData = {
            ...stepperData,
            ...newData,
        }

        setStepperData(newData)

        if (!changeStep) {
            return newData
        }

        goForward(newData)

        return newData
    }

    const onStepBack = (data: any) => {
        // With specific step set we are just submitting that step
        if (typeof specificStep === "number") {
            return
        }

        onStepSubmit(data, false)

        if (currentStepIndex === 0) {
            return
        }

        setCurrentStepIndex(currentStepIndex - 1)
    }

    return (
        <div className="relative">
            {props.withProgress && typeof specificStep === "undefined" ? (
                <div
                    className="flex items-center"
                    css={[styles.progressContainer]}
                >
                    <div css={styles.progressLeftWrapper}>
                        <div
                            css={[
                                styles.progressContainer,
                                styles.progressInner,
                            ]}
                        >
                            <Progress
                                percent={percentageOfProgress}
                                showInfo={false}
                                strokeColor={NIRVANA_COLORS.brand.primary}
                            />
                        </div>
                    </div>

                    <PrimaryText>
                        {currentStepIndex + 1} out of {props.steps.length}
                    </PrimaryText>
                </div>
            ) : null}

            {currentActiveStep &&
                stepsProcessed &&
                React.createElement(currentActiveStep.stepComponent, {
                    onStepBack,
                    onStepSubmit,
                    currentStepIndex,
                    defaultValues: stepperData[currentActiveStep.key],
                    fullStepperData: stepperData,
                    isSpecificStep: typeof specificStep === "number",
                } as IStepperStepProps)}

            {props.getSpecialStepBottomContent?.(currentStepIndex)}
        </div>
    )
}

const styles = {
    progressContainer: css({
        width: "100%",
        maxWidth: "588px",
        top: 0,
        zIndex: "20",
        left: "50%",
        background: NIRVANA_COLORS.brand.white,
        margin: "0 auto",
    }),
    progressLeftWrapper: css({
        flex: 1,
        marginRight: 20,
    }),
    progressInner: css({
        paddingLeft: 16,
        paddingRight: 16,
    }),
}

interface IStepperProps {
    steps: IStepperStep[]
    withProgress?: boolean
    onStepperSubmit: (data: any) => void
    getSpecialStepBottomContent?: (
        currentStepIndex: number,
    ) => React.JSX.Element | null
    searchParams?: URLSearchParams
}
